import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { ErrorState, useApplyFieldErrors } from '@nucleus-commerce/admin-kit/errors';
import type { InvitationPreview } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Label } from '@nucleus-commerce/ui/components/label';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { PasswordInput } from '@/shared/forms/password-input';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';
import { LEGAL_DOCUMENTS } from '@/shared/legal/legal-links';
import { session } from '@/shared/session/session';

import { AuthPage } from '../components/auth-page';
import { ServerError } from '../components/server-error';
import { useAcceptInvitation } from '../hooks/use-accept-invitation';
import { useInvitation } from '../hooks/use-invitation';
import { useSignInProviders } from '../hooks/use-sign-in-providers';
import {
  acceptInviteSchema,
  type AcceptInviteInput,
  type AcceptInviteOutput,
} from '../schemas/password-schemas';

export interface AcceptInvitePageProps {
  /** From the invitation email; missing or rejected tokens render the invalid state. */
  token: string | undefined;
}

/** A dead token; INVITATION_REQUIRES_LOGIN is also a 401 but means "sign in first". */
const isTokenError = (error: unknown) =>
  isApiError(error) && error.kind === 'unauthorized' && error.code !== 'INVITATION_REQUIRES_LOGIN';
const needsLogin = (error: unknown) =>
  isApiError(error) && error.code === 'INVITATION_REQUIRES_LOGIN';
const needsSecondFactor = (error: unknown) =>
  isApiError(error) && error.code === 'TWO_FACTOR_REQUIRED';

/**
 * Target of the invitation email. Accepting sets the session cookie, so a visitor who is
 * already signed in as someone else must sign out first; the same account just goes home.
 */
export function AcceptInvitePage({ token }: AcceptInvitePageProps) {
  const { t } = useTranslation('auth');
  const preview = useInvitation(token);
  const providers = useSignInProviders();
  const supportEmail = providers.data?.supportEmail ?? null;
  const { me, isPending: sessionPending } = session.useSession();
  const logout = session.useLogout();
  const accept = useAcceptInvitation();
  const navigate = useNavigate();
  const [tokenRejected, setTokenRejected] = useState(false);
  const backToSignIn = <Link to="/login">{t('links.backToSignIn')}</Link>;

  if (!token || tokenRejected || isTokenError(preview.error)) {
    return (
      <AuthPage
        title={t('accept.invalidTitle')}
        description={token ? t('accept.invalid') : t('accept.missingToken')}
        footer={backToSignIn}
      >
        <Alert>
          <AlertDescription className="flex flex-col gap-2">
            <span>{t('accept.askAdmin')}</span>
            <span data-testid="invite-support">
              {supportEmail ? (
                <Trans
                  t={t}
                  i18nKey="accept.support"
                  values={{ email: supportEmail }}
                  components={{
                    email: (
                      <a className="underline underline-offset-4" href={`mailto:${supportEmail}`}>
                        {supportEmail}
                      </a>
                    ),
                  }}
                />
              ) : (
                t('accept.supportFallback')
              )}
            </span>
          </AlertDescription>
        </Alert>
      </AuthPage>
    );
  }

  if (preview.isPending || sessionPending) {
    return (
      <AuthPage title={t('accept.loading')}>
        <div className="flex flex-col gap-3" aria-busy>
          <Skeleton className="h-11" />
          <Skeleton className="h-11" />
          <Skeleton className="h-11" />
        </div>
      </AuthPage>
    );
  }

  if (preview.isError) {
    return (
      <AuthPage title={t('accept.loadFailedTitle')} footer={backToSignIn}>
        <ErrorState error={preview.error} onRetry={() => void preview.refetch()} />
      </AuthPage>
    );
  }

  const invitation = preview.data;
  const title = t('accept.title', { organization: invitation.organizationName });
  const description = t('accept.description', { role: invitation.roleName });

  const sameAccount = me?.user.email.toLowerCase() === invitation.email.toLowerCase();
  if (me && sameAccount) {
    // Accepting while signed in re-issues the session for the new organisation; a second
    // organisation used to dead-end here with "head home" and the membership stayed invited.
    const acceptAndSwitch = () =>
      accept.mutate(
        { token },
        {
          onSuccess: () => void navigate({ to: '/org', replace: true }),
          onError: (error) => {
            if (isTokenError(error)) setTokenRejected(true);
          },
        },
      );
    return (
      <AuthPage
        title={t('accept.sameAccountTitle', { organization: invitation.organizationName })}
        description={description}
      >
        <div className="flex flex-col gap-4">
          <p className="text-muted-foreground text-sm">
            {t('accept.sameAccount', {
              email: me.user.email,
              organization: invitation.organizationName,
            })}
          </p>
          {needsSecondFactor(accept.error) ? (
            <Alert role="alert">
              <AlertDescription>{t('accept.twoFactorRequired')}</AlertDescription>
            </Alert>
          ) : accept.isError && !isTokenError(accept.error) ? (
            <ServerError error={accept.error} />
          ) : null}
          <Button
            size="lg"
            className="h-11 w-full"
            onClick={acceptAndSwitch}
            disabled={accept.isPending}
          >
            {accept.isPending ? t('accept.accepting') : t('accept.acceptAndSwitch')}
          </Button>
          <Button asChild variant="ghost" size="lg" className="h-11 w-full">
            <Link to="/org">{t('accept.stayHere')}</Link>
          </Button>
        </div>
      </AuthPage>
    );
  }

  if (me) {
    return (
      <AuthPage title={title} description={description}>
        <div className="flex flex-col gap-4">
          <Alert>
            <AlertDescription>
              {t('accept.signedInAs', {
                name: me.user.name,
                email: me.user.email,
                invitee: invitation.email,
              })}
            </AlertDescription>
          </Alert>
          <Button
            size="lg"
            className="h-11 w-full"
            onClick={() => logout.mutate()}
            disabled={logout.isPending}
          >
            {logout.isPending ? t('accept.signingOut') : t('accept.signOutAndContinue')}
          </Button>
        </div>
      </AuthPage>
    );
  }

  // An existing account accepts from its own session (the backend answers 401
  // INVITATION_REQUIRES_LOGIN otherwise), so send it through sign-in and back here.
  if (!invitation.requiresPassword) {
    const returnTo = `/accept-invite?token=${encodeURIComponent(token)}`;
    return (
      <AuthPage
        title={t('accept.requiresLoginTitle')}
        description={description}
        footer={backToSignIn}
      >
        <div className="flex flex-col gap-4">
          <p className="text-muted-foreground text-sm">
            {t('accept.requiresLogin', { email: invitation.email })}
          </p>
          <Button asChild size="lg" className="h-11 w-full">
            <Link
              to="/login"
              search={{ redirect: returnTo, organization: invitation.organizationSlug }}
            >
              {t('accept.signInToAccept', { email: invitation.email })}
            </Link>
          </Button>
        </div>
      </AuthPage>
    );
  }

  return (
    <AuthPage title={title} description={description} footer={backToSignIn}>
      <AcceptForm
        token={token}
        invitation={invitation}
        onTokenRejected={() => setTokenRejected(true)}
        onAccepted={() => navigate({ to: '/org', replace: true })}
      />
    </AuthPage>
  );
}

function AcceptForm({
  token,
  invitation,
  onTokenRejected,
  onAccepted,
}: {
  token: string;
  invitation: InvitationPreview;
  onTokenRejected: () => void;
  onAccepted: () => Promise<void>;
}) {
  const { t } = useTranslation('auth');
  const format = useFormat();
  const accept = useAcceptInvitation();
  const form = useForm<AcceptInviteInput, unknown, AcceptInviteOutput>({
    resolver: useI18nResolver(acceptInviteSchema),
    defaultValues: {
      name: invitation.name,
      requiresPassword: invitation.requiresPassword,
      password: '',
      confirmPassword: '',
    },
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await accept.mutateAsync({
        token,
        name: values.name,
        ...(values.requiresPassword ? { password: values.password } : {}),
      });
      await onAccepted();
    } catch (error) {
      if (isTokenError(error)) {
        onTokenRejected();
        return;
      }
      if (needsLogin(error)) return;
      applyFieldErrors(error);
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
        {needsLogin(accept.error) ? (
          <Alert role="alert">
            <AlertDescription>
              {t('accept.requiresLogin', { email: invitation.email })}{' '}
              <Link to="/login" className="underline underline-offset-4">
                {t('accept.signInToAccept', { email: invitation.email })}
              </Link>
            </AlertDescription>
          </Alert>
        ) : accept.isError && !accept.error.hasFieldErrors ? (
          <ServerError error={accept.error} />
        ) : null}
        <div className="flex flex-col gap-2">
          <Label htmlFor="invitation-email">{t('fields.email')}</Label>
          <Input
            id="invitation-email"
            value={invitation.email}
            readOnly
            className="bg-muted/60 h-11"
          />
          <p className="text-muted-foreground text-sm">{t('accept.emailHint')}</p>
        </div>
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('fields.name')}</FormLabel>
              <FormControl>
                <Input autoComplete="name" className="h-11" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {invitation.requiresPassword ? (
          <>
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.password')}</FormLabel>
                  <FormControl>
                    <PasswordInput autoComplete="new-password" className="h-11" {...field} />
                  </FormControl>
                  <FormDescription>{t('accept.passwordHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.confirmPassword')}</FormLabel>
                  <FormControl>
                    <PasswordInput autoComplete="new-password" className="h-11" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        ) : null}
        <p className="text-muted-foreground text-xs leading-relaxed" data-testid="invite-legal">
          <Trans
            t={t}
            i18nKey="accept.legal"
            components={{
              privacy: (
                <a
                  className="text-foreground underline underline-offset-4"
                  href={LEGAL_DOCUMENTS.privacy}
                  target="_blank"
                  rel="noreferrer"
                />
              ),
            }}
          />
        </p>
        <Button type="submit" size="lg" className="h-11 w-full" disabled={accept.isPending}>
          {accept.isPending ? t('accept.submitting') : t('accept.submit')}
        </Button>
        <p className="text-muted-foreground text-center text-xs">
          {t('accept.expiresAt', { date: format.dateTime(invitation.expiresAt) })}
        </p>
      </form>
    </Form>
  );
}
